import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { buttonHierarchies } from "../content-builder/element-schema";
import { ImageType } from "../image-type";
import {
  defaultPrimaryButtonConfiguration,
  defaultSecondaryButtonConfiguration,
  trackingPagePageButtonSettingsDefault,
} from "./tracking-page-defaults";

export const gameDifficultySchema = createEnum(["easy", "medium", "hard"]);
export type GameDifficulty = z.infer<typeof gameDifficultySchema>;

export const TRACKING_PAGE_GRID_COLUMNS = 7;
export const TRACKING_PAGE_GRID_ROWS = 9;
export const TRACKING_PAGE_GRID_CELL_SIZE = 160;
export const TRACKING_PAGE_GRID_GAP = 16;

export enum TrackingPageBlockType {
  HEADER = "header",
  TRACKING_DETAILS = "tracking-details",
  ITEMS_IN_FULFILLMENT = "items-in-fulfillment",
  PRODUCT_UPSELL = "product-upsell",
  PROMOTION = "promotion",
  GAME = "game",
  SUBSCRIPTION_UPSELL = "subscription-upsell",
  TRACKING_UPDATES = "tracking-updates",
}

export enum SubscriptionUpsellFallbackCondition {
  CUSTOMER_IS_SUBSCRIBED = "customer-is-subscribed",
}

export enum PromotionType {
  PAGE = "page",
  SUBSCRIBE = "subscribe",
}

export const DEFAULT_BLOCK_SIZES: Record<
  TrackingPageBlockType,
  { width: number; height: number }
> = {
  [TrackingPageBlockType.HEADER]: { width: 7, height: 1 },
  [TrackingPageBlockType.TRACKING_DETAILS]: { width: 2, height: 3 },
  [TrackingPageBlockType.ITEMS_IN_FULFILLMENT]: { width: 5, height: 3 },
  [TrackingPageBlockType.PRODUCT_UPSELL]: { width: 3, height: 3 },
  [TrackingPageBlockType.PROMOTION]: { width: 2, height: 3 },
  [TrackingPageBlockType.GAME]: { width: 2, height: 3 },
  [TrackingPageBlockType.SUBSCRIPTION_UPSELL]: { width: 3, height: 3 },
  [TrackingPageBlockType.TRACKING_UPDATES]: { width: 3, height: 1 },
};

export const MIN_BLOCK_SIZES: Record<
  TrackingPageBlockType,
  { width: number; height: number }
> = {
  [TrackingPageBlockType.HEADER]: { width: 1, height: 1 },
  [TrackingPageBlockType.TRACKING_DETAILS]: { width: 2, height: 2 },
  [TrackingPageBlockType.ITEMS_IN_FULFILLMENT]: { width: 2, height: 2 },
  [TrackingPageBlockType.PRODUCT_UPSELL]: { width: 2, height: 2 },
  [TrackingPageBlockType.PROMOTION]: { width: 1, height: 1 },
  [TrackingPageBlockType.GAME]: { width: 2, height: 2 },
  [TrackingPageBlockType.SUBSCRIPTION_UPSELL]: { width: 3, height: 3 },
  [TrackingPageBlockType.TRACKING_UPDATES]: { width: 3, height: 1 },
};

export enum GameType {
  GALAGA = "galaga",
  CLEANUP_CREW = "cleanup-crew",
  DELIVERY_DASH = "delivery-dash",
  FLAPPY_FREIGHT = "flappy-freight",
  TINY_PACKAGE = "tiny-package",
}

export const prettyGameType = {
  [GameType.GALAGA]: "Galaga",
  [GameType.CLEANUP_CREW]: "Cleanup Crew",
  [GameType.DELIVERY_DASH]: "Delivery Dash",
  [GameType.FLAPPY_FREIGHT]: "Flappy Freight",
  [GameType.TINY_PACKAGE]: "Tiny Package",
};

const colorSwatchesValidator = z.object({
  50: z.string(),
  100: z.string(),
  200: z.string(),
  300: z.string(),
  400: z.string(),
  500: z.string(),
  600: z.string(),
  700: z.string(),
  800: z.string(),
  900: z.string(),
  950: z.string(),
});
export interface ColorSwatches extends z.infer<typeof colorSwatchesValidator> {}

export type ColorSwatchKey = keyof z.infer<typeof colorSwatchesValidator>;

export const colorStops = Object.keys(colorSwatchesValidator.shape).map(
  Number,
) as Array<ColorSwatchKey>;

const paletteThemeValidator = z.object({
  baseColor: z.string(),
  valueStop: z.number(),
  colorSwatches: colorSwatchesValidator,
});
export interface PaletteTheme extends z.infer<typeof paletteThemeValidator> {}

export const orderTrackingPageButtonHierarchies = [
  ...buttonHierarchies,
  "custom",
] as const;
export type OrderTrackingPageButtonHierarchy =
  (typeof orderTrackingPageButtonHierarchies)[number];

const buttonConfigurationStateSchema_ = z.object({
  style: z.object({ background: z.string(), border: z.string() }),
  text: z.object({ fontColor: z.string() }),
});
const buttonConfigurationStateSchema: z.ZodType<ButtonConfigurationState> =
  buttonConfigurationStateSchema_;
interface ButtonConfigurationState
  extends z.infer<typeof buttonConfigurationStateSchema_> {}

const buttonConfigurationSchema_ = z.object({
  default: buttonConfigurationStateSchema,
  hovered: buttonConfigurationStateSchema.optional(),
  active: buttonConfigurationStateSchema.optional(),
});
const buttonConfigurationSchema: z.ZodType<ButtonConfiguration> =
  buttonConfigurationSchema_;
interface ButtonConfiguration
  extends z.infer<typeof buttonConfigurationSchema_> {}

const pageButtonSettingsSchema_ = z.object({
  primary: buttonConfigurationSchema,
  secondary: buttonConfigurationSchema,
  tertiary: buttonConfigurationSchema,
  palette: paletteThemeValidator,
});
export const pageButtonSettingsSchema: z.ZodType<PageButtonSettings> =
  pageButtonSettingsSchema_;
export interface PageButtonSettings
  extends z.infer<typeof pageButtonSettingsSchema_> {}

const buttonSettingsSchema_ = z.object({
  hierarchy: createEnum(orderTrackingPageButtonHierarchies),
  buttonConfiguration: buttonConfigurationSchema,
});
const buttonSettingsSchema: z.ZodType<ButtonSettings> = buttonSettingsSchema_;
export interface ButtonSettings extends z.infer<typeof buttonSettingsSchema_> {}

export const baseTrackingPageBlockSchema = z.object({
  _id: zExt.objectId(),
  type: createEnum(Object.values(TrackingPageBlockType)),
  anchorCoordinates: z.object({ x: z.number(), y: z.number() }),
  /**
   * The size of the block in the grid.
   * 1 x 1, 2 x 1, 1 x 2, 2 x 2, etc.
   */
  size: z.object({ width: z.number(), height: z.number() }),
});

export const trackingPageHeaderBlockSchema = baseTrackingPageBlockSchema.extend(
  { type: z.literal(TrackingPageBlockType.HEADER) },
);
export type TrackingPageHeaderBlock = z.infer<
  typeof trackingPageHeaderBlockSchema
>;

export const trackingPageTrackingDetailsBlockSchema =
  baseTrackingPageBlockSchema.extend({
    type: z.literal(TrackingPageBlockType.TRACKING_DETAILS),
    optionalSubtext: z.string().optional(),
  });
export type TrackingPageTrackingDetailsBlock = z.infer<
  typeof trackingPageTrackingDetailsBlockSchema
>;

export const trackingPageItemsInFulfillmentBlockSchema =
  baseTrackingPageBlockSchema.extend({
    type: z.literal(TrackingPageBlockType.ITEMS_IN_FULFILLMENT),
    imageAspectRatio: z.number().optional(),
    hidePrice: z.boolean().optional(),
    hideQuantity: z.boolean().optional(),
    buttonSettings: buttonSettingsSchema
      .optional()
      .default({
        hierarchy: "secondary",
        buttonConfiguration: defaultSecondaryButtonConfiguration,
      }),
  });
export type TrackingPageItemsInFulfillmentBlock = z.infer<
  typeof trackingPageItemsInFulfillmentBlockSchema
>;

export const trackingPageProductUpsellBlockSchema =
  baseTrackingPageBlockSchema.extend({
    type: z.literal(TrackingPageBlockType.PRODUCT_UPSELL),
    recommendedProductFilterId: z.string().nullish(),
    upsellText: z.string().optional(),
    imageAspectRatio: z.number().optional(),
    discountId: zExt.objectId().optional(),
    checkoutButtonSettings: buttonSettingsSchema
      .optional()
      .default({
        hierarchy: "secondary",
        buttonConfiguration: defaultSecondaryButtonConfiguration,
      }),
    dropdownSettings: buttonSettingsSchema
      .optional()
      .default({
        hierarchy: "secondary",
        buttonConfiguration: defaultSecondaryButtonConfiguration,
      }),
  });
export type TrackingPageProductUpsellBlock = z.infer<
  typeof trackingPageProductUpsellBlockSchema
>;

export const trackingPagePromotionBlockSchema =
  baseTrackingPageBlockSchema.extend({
    type: z.literal(TrackingPageBlockType.PROMOTION),
    imageUrl: z.string().optional(),
    url: z.string().optional(),
    altText: z.string().optional(),
    promotionType: createEnum(Object.values(PromotionType)).optional(),
    imageSourceType: createEnum(Object.values(ImageType)).optional(),
  });
export type TrackingPagePromotionBlock = z.infer<
  typeof trackingPagePromotionBlockSchema
>;

export const trackingPageSubscriptionUpsellBlockSchema =
  baseTrackingPageBlockSchema.extend({
    type: z.literal(TrackingPageBlockType.SUBSCRIPTION_UPSELL),
    fallbackCondition: createEnum(
      Object.values(SubscriptionUpsellFallbackCondition),
    ).optional(),
    fallbackBlock: createEnum(Object.values(TrackingPageBlockType)).optional(),
    // For now, we only support promotion blocks as fallback blocks
    fallbackBlockSettings: trackingPagePromotionBlockSchema.optional(),
    title: z.string().optional(),
    descriptionOverride: z.string().optional(),
    subscriptionProductId: z.string().nullish(),
    perks: z.array(z.string()).optional(),
    orderIntervalFrequency: z.number().optional(),
    chargeIntervalFrequency: z.number().optional(),
    isEditingFallback: z.boolean().optional(),
    buttonSettings: buttonSettingsSchema
      .optional()
      .default({
        hierarchy: "primary",
        buttonConfiguration: defaultPrimaryButtonConfiguration,
      }),
    secondaryButtonSettings: buttonSettingsSchema
      .optional()
      .default({
        hierarchy: "secondary",
        buttonConfiguration: defaultSecondaryButtonConfiguration,
      }),
    dropdownSettings: buttonSettingsSchema
      .optional()
      .default({
        hierarchy: "secondary",
        buttonConfiguration: defaultSecondaryButtonConfiguration,
      }),
  });
export type TrackingPageSubscriptionUpsellBlock = z.infer<
  typeof trackingPageSubscriptionUpsellBlockSchema
>;

const gameRewardSettingsSchema_ = z.object({
  discountId: z.string().optional(),
  showRewardOnVictory: z.boolean(),
  rewardMessage: z.string().optional(),
});
export const gameRewardSettingsSchema: z.ZodType<GameRewardSettings> =
  gameRewardSettingsSchema_;
export interface GameRewardSettings
  extends z.infer<typeof gameRewardSettingsSchema_> {}

export const trackingPageTrackingUpdatesBlockSchema =
  baseTrackingPageBlockSchema.extend({
    type: z.literal(TrackingPageBlockType.TRACKING_UPDATES),
    title: z.string().optional(),
    buttonSettings: buttonSettingsSchema
      .optional()
      .default({
        hierarchy: "primary",
        buttonConfiguration: defaultPrimaryButtonConfiguration,
      }),
  });
export type TrackingPageTrackingUpdatesBlock = z.infer<
  typeof trackingPageTrackingUpdatesBlockSchema
>;

// Base game block schema with shared properties
const baseGameBlockSchema = baseTrackingPageBlockSchema.extend({
  type: z.literal(TrackingPageBlockType.GAME),
  gameSettings: z.object({}).nullish(),
  rewardSettings: z.object({}).nullish(),
});

// Galaga game block schema
export const trackingPageGalagaGameBlockSchema = baseGameBlockSchema.extend({
  gameType: z.literal(GameType.GALAGA),
  difficulty: gameDifficultySchema.optional(),
});
export type TrackingPageGalagaGameBlock = z.infer<
  typeof trackingPageGalagaGameBlockSchema
>;

// Delivery Dash game block schema
export const trackingPageDeliveryDashGameBlockSchema =
  baseGameBlockSchema.extend({
    gameType: z.literal(GameType.DELIVERY_DASH),
    rewardSettings: gameRewardSettingsSchema,
  });
export type TrackingPageDeliveryDashGameBlock = z.infer<
  typeof trackingPageDeliveryDashGameBlockSchema
>;

// Cleanup crew game block schema
export const trackingPageCleanupCrewGameBlockSchema =
  baseGameBlockSchema.extend({
    gameType: z.literal(GameType.CLEANUP_CREW),
    gameSettings: z.object({
      selectedLevel: z.number(),
      useRandomLevel: z.boolean(),
    }),
    rewardSettings: gameRewardSettingsSchema,
  });
export type TrackingPageCleanupCrewGameBlock = z.infer<
  typeof trackingPageCleanupCrewGameBlockSchema
>;

// Flappy freight game block schema
export const trackingPageFlappyFreightGameBlockSchema =
  baseGameBlockSchema.extend({
    gameType: z.literal(GameType.FLAPPY_FREIGHT),
    rewardSettings: gameRewardSettingsSchema,
  });
export type TrackingPageFlappyFreightGameBlock = z.infer<
  typeof trackingPageFlappyFreightGameBlockSchema
>;

export const trackingPageTinyPackageGameBlockSchema =
  baseGameBlockSchema.extend({
    gameType: z.literal(GameType.TINY_PACKAGE),
    gameSettings: z.object({
      difficulty: gameDifficultySchema.optional(),
      playerSpriteUrl: z.string().optional().nullish(),
      playerSpriteSize: z.number().optional().nullish(),
      npcSpriteUrl: z.string().optional().nullish(),
      npcSpriteSize: z.number().optional().nullish(),
    }),
    rewardSettings: gameRewardSettingsSchema,
  });
export type TrackingPageTinyPackageGameBlock = z.infer<
  typeof trackingPageTinyPackageGameBlockSchema
>;

// Discriminated union of all game block types
export const trackingPageGameBlockSchema = z.discriminatedUnion("gameType", [
  trackingPageGalagaGameBlockSchema,
  trackingPageCleanupCrewGameBlockSchema,
  trackingPageDeliveryDashGameBlockSchema,
  trackingPageFlappyFreightGameBlockSchema,
  trackingPageTinyPackageGameBlockSchema,
]);
export type TrackingPageGameBlock = z.infer<typeof trackingPageGameBlockSchema>;

const trackingPageBlockSchema_ = z.discriminatedUnion("type", [
  trackingPageHeaderBlockSchema,
  trackingPageTrackingDetailsBlockSchema,
  trackingPageItemsInFulfillmentBlockSchema,
  trackingPageProductUpsellBlockSchema,
  trackingPagePromotionBlockSchema,
  trackingPageGameBlockSchema,
  trackingPageSubscriptionUpsellBlockSchema,
  trackingPageTrackingUpdatesBlockSchema,
]);
export const trackingPageBlockSchema: z.ZodType<TrackingPageBlock> =
  trackingPageBlockSchema_;
export type TrackingPageBlock = z.infer<typeof trackingPageBlockSchema_>;

const trackingPageSettingsSchema_ = z.object({
  name: z.string().optional(),
  blocks: z.array(trackingPageBlockSchema),
  background: z.object({ backgroundColor: z.string() }),
  typography: z.object({
    headerFontFamily: z.string(),
    headerFontColor: z.string(),
    bodyFontFamily: z.string(),
    bodyFontColor: z.string(),
    linkFontColor: z.string().optional(),
  }),
  blockSettings: z.object({
    backgroundColor: z.string(),
    borderRadius: z.number(),
    borderColor: z.string().optional(),
  }),
  pageButtonSettings: pageButtonSettingsSchema
    .optional()
    .default(trackingPagePageButtonSettingsDefault()),
});

export const trackingPageSettingsSchema: z.ZodType<TrackingPageSettings> =
  trackingPageSettingsSchema_;
export interface TrackingPageSettings
  extends z.infer<typeof trackingPageSettingsSchema_> {}

const trackingPageSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  settings: trackingPageSettingsSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const trackingPageSchema: z.ZodType<TrackingPage> = trackingPageSchema_;
export interface TrackingPage extends z.infer<typeof trackingPageSchema_> {}
